import { AnchorHTMLAttributes, ButtonHTMLAttributes, createContext, KeyboardEvent, ReactNode, useCallback, useContext, useRef } from "react";
import { Link, useInRouterContext } from "react-router-dom";
import Popover from "@mui/material/Popover";
import Modal from "@mui/material/Modal";
import { usePhone } from "../usePhone";
import { ToastHost } from "../Toast";
import s from "./MenuSurface.module.scss";

const SheetContext = createContext(false);

type Props = {
  anchorEl: HTMLElement | null; open: boolean; onClose: () => void; label: string;
  /** Plain content above the items. On a phone it labels the sheet when it contains the element with id `labelledBy`. */
  header?: ReactNode; labelledBy?: string; children: ReactNode;
  /** Desktop popover only. "above-start": opens upward, aligned to the button's inline start (for buttons at the bottom of a side column). */
  placement?: "below-end" | "above-start";
};

/**
 * A small popup menu. Desktop: a popover with role="menu" (opens on the first item; arrows wrap, Home/End jump,
 * Tab or Escape closes, focus returns to the button). Phone: the same header and items in a bottom sheet (role="dialog").
 */
export const MenuSurface = ({ anchorEl, open, onClose, label, header, labelledBy, children, placement = "below-end" }: Props) => {
  const phone = usePhone();
  const list = useRef<HTMLDivElement | null>(null);
  const items = () => Array.from(list.current?.querySelectorAll<HTMLElement>('[role^="menuitem"]:not(:disabled)') ?? []);
  // The popup mounts after the first render, so focus the first item when the list node attaches (stable callback: not on re-renders).
  const attach = useCallback((node: HTMLDivElement | null) => {
    list.current = node;
    node?.querySelector<HTMLElement>('[role^="menuitem"]')?.focus();
  }, []);
  // In a real browser the node can attach before the popover is on screen, where focus() does nothing: retry once it has entered.
  const focusFirst = () => { if (!list.current?.contains(document.activeElement)) list.current?.querySelector<HTMLElement>('[role^="menuitem"]')?.focus(); };
  const onKeyDown = (e: KeyboardEvent) => {
    const all = items();
    const at = all.indexOf(document.activeElement as HTMLElement);
    const go = (i: number) => { e.preventDefault(); all[(i + all.length) % all.length]?.focus(); };
    if (e.key === "ArrowDown") go(at + 1);
    else if (e.key === "ArrowUp") go(at <= 0 ? -1 : at - 1);
    else if (e.key === "Home") go(0);
    else if (e.key === "End") go(-1);
    else if (e.key === "Tab") onClose();
  };

  if (phone) {
    return (
      <Modal open={open} onClose={onClose} className="cy-next">
        <section role="dialog" aria-modal="true" aria-label={labelledBy ? undefined : label} aria-labelledby={labelledBy} className={s.sheet}>
          <div aria-hidden="true" className={s.handle} />
          {header}
          <SheetContext.Provider value><div className={s.sheetList}>{children}</div></SheetContext.Provider>
          <ToastHost active={open} />
        </section>
      </Modal>
    );
  }
  return (
    <Popover open={open} anchorEl={anchorEl} onClose={onClose} className="cy-next" disableAutoFocus TransitionProps={{ onEntered: focusFirst }}
      anchorOrigin={placement === "above-start" ? { vertical: "top", horizontal: "left" } : { vertical: "bottom", horizontal: "right" }}
      transformOrigin={placement === "above-start" ? { vertical: "bottom", horizontal: "left" } : { vertical: "top", horizontal: "right" }}
      PaperProps={{ className: s.paper }}>
      {header}
      <div ref={attach} role="menu" aria-label={label} className={s.list} onKeyDown={onKeyDown}>{children}</div>
      <ToastHost active={open} />
    </Popover>
  );
};

type ItemProps = { icon?: ReactNode; checked?: boolean; selected?: boolean } & ButtonHTMLAttributes<HTMLButtonElement>;
/**
 * `checked` makes it an on/off entry: menuitemcheckbox in the menu, a switch in the phone sheet.
 * `selected` makes it one choice of a group: menuitemradio in the menu, a radio in the phone sheet.
 */
export const MenuSurfaceItem = ({ icon, checked, selected, children, className, ...rest }: ItemProps) => {
  const sheet = useContext(SheetContext);
  const aria = selected !== undefined
    ? { role: sheet ? "radio" : "menuitemradio", "aria-checked": selected }
    : checked === undefined
      ? { role: sheet ? undefined : "menuitem" }
      : { role: sheet ? "switch" : "menuitemcheckbox", "aria-checked": checked };
  return (
    <button type="button" className={[s.item, className ?? ""].join(" ").trim()} {...aria} {...rest}>
      {icon && <span aria-hidden="true" className={s.icon}>{icon}</span>}
      {children}
    </button>
  );
};

/** A path inside this app ("/x"), not an absolute or protocol-relative URL. */
const isInternal = (href?: string): href is string => !!href && href.startsWith("/") && !href.startsWith("//");

/**
 * A menu entry that navigates: a real link, styled like the buttons. Inside a router an app path goes through
 * the router (no page load); anything else, or outside a router, is a plain <a>.
 */
export const MenuSurfaceLink = ({ icon, children, className, href, ...rest }: { icon?: ReactNode } & AnchorHTMLAttributes<HTMLAnchorElement>) => {
  const sheet = useContext(SheetContext);
  const inRouter = useInRouterContext();
  const props = { className: [s.item, className ?? ""].join(" ").trim(), role: sheet ? undefined : "menuitem", ...rest };
  const body = <>{icon && <span aria-hidden="true" className={s.icon}>{icon}</span>}{children}</>;
  return inRouter && isInternal(href) ? <Link to={href} {...props}>{body}</Link> : <a href={href} {...props}>{body}</a>;
};
